import mime from 'mime/lite';
import type { PartialFile } from '../types';
import {
  MEDIA_FILE_QUOTED_REGEX,
  MEDIA_FILE_REGEX,
  NOT_EXTERNAL_LINK_REGEX,
  STRING_REGEX,
} from '../../util/file-utils';

interface CustomFile {
  name: string;
  path: string;
  content: string;
}

/**
 * TODO 자바스크립트 파일안에 있는 파일명을 blob로 대체할지는 판단이 필요하다.
 * 이미지나 mp3등은
 */
export class HtmlFilePathResolver {
  // "/sketch.js" to CustomFile
  private fileStore = new Map<string, CustomFile>();

  // "/sketch.js" to blobUrl
  private blobUrlStore = new Map<string, string>();
  private origin?: string;
  private unresolvedDeps: Record<string, Set<string>> = {};

  constructor(inputFiles: CustomFile[], origin?: string) {
    this.origin = origin;
    inputFiles.forEach((f) => {
      const newFile: CustomFile = {
        name: f.name,
        path: normalizeUrlPath(f.path, origin),
        content: f.content,
      };
      this.fileStore.set(newFile.path, newFile);
      // TODO 이미지 등의 미디어 파일은 blobUrlStore에 즉시 저장
      return newFile;
    });
  }

  /**
   * 결과 blob url 목록
   */
  get resultBlobUrls(): string[] {
    return Array.from(this.blobUrlStore.values());
  }

  /**
   * 결과 PartialFile 목록
   */
  get resultFiles(): PartialFile[] {
    const results = [] as PartialFile[];
    for (const entry of this.fileStore.values()) {
      results.push({
        ...entry,
        blobUrl: this.blobUrlStore.get(entry.path),
      });
    }
    return results;
  }

  /**
   * 입력 파일 목록
   */
  get sourceFiles(): CustomFile[] {
    return Array.from(this.fileStore.values());
  }

  /**
   *
   * @param entryPointFilePath
   */
  start(entryPointFilePath: string): Document {
    const indexHtml = this.fileStore.get(entryPointFilePath);
    if (!indexHtml) {
      throw new Error('주어진 진입점 파일은 존재하지 않습니다:' + entryPointFilePath);
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(indexHtml.content, 'text/html');

    // 디펜던시를 탐색하고
    this.searchDeps_(doc, indexHtml);

    // blob url을 만들면서 디펜던시를 해결한다
    this.convertFilePathToBlobUrl_(doc);
    return doc;
  }

  /**
   * 디펜던시를 추가한다.
   * 만약 이미 blobURL로 만들어졌다면, blobURL을 리턴한다.
   * blobURL이 만들어졌다면, 이 파일은 디펜던시가 해결된 상태를 의미한다.
   */
  private addDeps = (targetFilePath: string, deps: string): string | undefined => {
    if (this.blobUrlStore.has(deps)) {
      // 이 deps는 디펜던시가 해결되었음
      return this.blobUrlStore.get(deps);
    }

    // 자기 자신이 디펜던시인 경우 무시
    if (targetFilePath === deps) {
      console.warn(`ignore, self referenced: ${targetFilePath}`);
      return undefined;
    }

    // 등록되지 않은 파일은 deps에서 무시
    if (!this.fileStore.has(deps)) {
      console.log('ignore unknown file: ' + deps);
      return undefined;
    }

    // 미해결 디펜던시에 추가
    let set = this.unresolvedDeps[targetFilePath];
    if (!set) {
      set = new Set();
      this.unresolvedDeps[targetFilePath] = set;
    }
    set.add(deps);

    if (!this.unresolvedDeps[deps]) {
      this.unresolvedDeps[deps] = new Set();
    }

    return undefined;
  };

  /**
   * 파일의 내용을 업데이트 한다
   */
  private updateFileContent_ = (
    filePath: string,
    contentOrFn: ((prev: string) => string) | string,
  ) => {
    const f = this.fileStore.get(filePath);
    if (!f) {
      console.warn('cannot find file:', filePath);
      return;
    }
    f.content = typeof contentOrFn === 'function' ? contentOrFn(f.content) : contentOrFn;
  };

  /**
   * html 파일로부터 디펜던시를 찾는다.
   * 더이상 발견되지 않을때까지, recursive 하게 탐색한다.
   */
  private searchDeps_ = (doc: Document, htmlFile: CustomFile) => {
    this.resolveHtmlFile(doc, htmlFile);

    const checkedSet = new Set<string>();
    checkedSet.add(htmlFile.path);
    for (;;) {
      let newCnt = 0;
      Object.keys(this.unresolvedDeps).forEach((path) => {
        if (!checkedSet.has(path)) {
          checkedSet.add(path);
          this.resolveFile_(doc, this.fileStore.get(path)!);
          newCnt++;
        }
      });
      if (newCnt === 0) {
        break;
      }
    }
  };

  /**
   * 파일 경로 디펜던시들을 blob url로 만든다.
   */
  private convertFilePathToBlobUrl_ = (doc: Document) => {
    for (let i = 0; i < 100; i++) {
      if (Object.keys(this.unresolvedDeps).length === 0) {
        break;
      }

      // deps가 없으면, 해당 항목의 blobUrl을 만들고 제거
      // deps가 있는데, 모두 resolved되었다면 그 파일을 파싱하여, blobUrl로 대체한다

      const toRemoveFilePaths = [] as string[];
      for (const [filePath, depsSet] of Object.entries(this.unresolvedDeps)) {
        if (depsSet.size === 0) {
          // 디펜던시 해결완료, 해결할 deps가 없다.
          this.registerBlobURL_(filePath);

          // 제거 대상에 추가
          toRemoveFilePaths.push(filePath);
        }
      }

      // 제거 대상들 삭제
      for (const filePath of toRemoveFilePaths) {
        delete this.unresolvedDeps[filePath];
      }
      toRemoveFilePaths.length = 0;

      for (const [filePath, depsSet] of Object.entries(this.unresolvedDeps)) {
        if (Array.from(depsSet).every((it) => this.blobUrlStore.has(it))) {
          // filePath의 모든 디펜던시들의 blobUrl이 존재한다.
          // filePath의 파일 내용에서 파일 경로를 blobUrl로 대체한다.
          this.resolveFile_(doc, this.fileStore.get(filePath)!);

          // filePath의 디펜던시 해결 완료, filePath의 blobUrl을 등록한다.
          this.registerBlobURL_(filePath);

          // 제거 대상에 추가
          toRemoveFilePaths.push(filePath);
        }
      }

      // 제거 대상들 삭제
      for (const filePath of toRemoveFilePaths) {
        delete this.unresolvedDeps[filePath];
      }
    }
  };

  private resolveFile_ = (doc: Document, file: CustomFile) => {
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.html') || fileName.endsWith('.htm')) {
      return this.resolveHtmlFile(doc, file);
    }
    if (fileName.endsWith('.css')) {
      return this.resolveCssFile_(file);
    }

    if (fileName.endsWith('.js')) {
      return this.resolveJsFile_(file);
    }
    return 0;
  };

  private resolveHtmlFile = (doc: Document, htmlFile: CustomFile): number => {
    let newFileCnt = 0; // 신규로 발견한 deps 개수를 리턴한다.

    // 스크립트 태그
    newFileCnt += this.resolveDepsFromScriptTag_(doc, (targetPath) =>
      this.addDeps(htmlFile.path, targetPath),
    );

    // style 태그의 inline css 파싱
    const styleElems = Array.from(doc.getElementsByTagName('style'));
    styleElems.forEach((styleElem) => {
      newFileCnt += this.resolveDepsFromCssContents_(styleElem, styleElem.innerHTML, (targetPath) =>
        this.addDeps(htmlFile.path, targetPath),
      );
    });

    // 스타일시트 링크태그 처리
    newFileCnt += this.resolveDepsFromStylesheetLinkTag_(doc, (targetPath) =>
      this.addDeps(htmlFile.path, targetPath),
    );

    // html내의 a.href, img.src 등을 탐색하기
    // script와 css는 모두 탐색했으므로 무시
    // a.href

    // html내의 a.href,img.src 등을 탐색하기
    newFileCnt += this.resolveMediaDepsFromHtmlContent_(doc, (targetPath) =>
      this.addDeps(htmlFile.path, targetPath),
    );
    return newFileCnt;
  };

  private resolveJsFile_ = (file: CustomFile): number => {
    return this.resolveDepsFromJsContents_(file.path, file.content, (targetPath) =>
      this.addDeps(file.path, targetPath),
    );
  };

  private resolveCssFile_ = (file: CustomFile): number => {
    return this.resolveDepsFromCssContents_(file.path, file.content, (targetPath) =>
      this.addDeps(file.path, targetPath),
    );
  };

  /**
   * HTML 문서내에서 미디어 파일의 디펜던시를 찾거나 해결한다.
   * a.href,img.src 는 js나 css 파일일 수 없으므로
   * 미디어 파일만 처리한다.
   */
  private resolveMediaDepsFromHtmlContent_ = (
    doc: Document,
    callback: (path: string) => string | undefined,
  ): number => {
    let newFileCnt = 0;
    // html내의 a.href,img.src 등을 탐색하기
    // script와 css는 모두 탐색했으므로 무시
    const ignoreTags = ['SCRIPT', 'LINK'];
    ['src', 'href'].forEach((attr) => {
      const srcElems: Element[] = Array.from(doc.querySelectorAll(`[${attr}]`));
      srcElems.forEach((elem) => {
        if (!ignoreTags.includes(elem.tagName)) {
          const val = elem.getAttribute(attr);
          if (val && val.match(MEDIA_FILE_REGEX)) {
            const path = normalizeUrlPath(val);
            if (this.fileStore.has(path) && !this.unresolvedDeps[path]) {
              // 신규 deps 발견
              newFileCnt++;
            }
            const result = callback(path);
            if (result) {
              elem.setAttribute(attr, result);
            }
          }
        }
      });
    });
    return newFileCnt;
  };

  /**
   * script 태그에서 디펜던시를 찾거나 해결한다
   */
  private resolveDepsFromScriptTag_ = (
    doc: Document,
    callback: (path: string) => string | undefined,
  ): number => {
    let newFileCnt = 0;
    Array.from(doc.getElementsByTagName('script')).forEach((script) => {
      let src = script.getAttribute('src');
      if (src) {
        if (src.match(NOT_EXTERNAL_LINK_REGEX)) {
          // 로컬 스크립트 파일 링크
          src = normalizeUrlPath(src, this.origin);
          // console.log("로컬 스크립트 파일 링크:", src);
          if (this.fileStore.has(src) && !this.unresolvedDeps[src]) {
            // 신규 deps 발견
            newFileCnt++;
          }
          const newSrc = callback(src);
          if (newSrc) {
            script.src = newSrc;
          }
        } else {
          // 외부 스크립트 링크
          // console.log("외부 스크립트 링크:", src);
          script.setAttribute('crossorigin', '');
        }
      } else {
        // 인라인 스크립트
        // js 소스코드에서 문자열로 참조하는게 있는지 체크
        newFileCnt += this.resolveDepsFromJsContents_(script, script.innerHTML, callback);
      }
    });
    return newFileCnt;
  };

  /**
   * 자바스크립트 내용에서 디펜던시를 찾는다
   */
  private resolveDepsFromJsContents_ = (
    filePathOrElem: string | HTMLScriptElement,
    jsContent: string,
    callback: (path: string) => string | undefined,
  ) => {
    let newFileCnt = 0;
    // 인라인 스크립트
    // js 소스코드에서 문자열로 참조하는게 있는지 체크
    const matchedResult = jsContent.match(STRING_REGEX);
    if (matchedResult) {
      matchedResult
        .filter((str) => str.match(MEDIA_FILE_QUOTED_REGEX))
        .forEach((str) => {
          const depFilePath = normalizeUrlPath(str.slice(1, -1)); // remove quotes
          if (this.fileStore.has(depFilePath) && !this.unresolvedDeps[depFilePath]) {
            // 신규 deps 발견
            newFileCnt++;
          }

          const result = callback(depFilePath);
          if (result) {
            const quoteChar = str[0]!;
            const newStr = quoteChar + result + quoteChar;
            if (typeof filePathOrElem === 'string') {
              this.updateFileContent_(filePathOrElem, (prev) => prev.replace(str, newStr));
            } else {
              filePathOrElem.innerHTML = filePathOrElem.innerHTML.replace(str, newStr);
            }
          }
        });
    }
    return newFileCnt;
  };

  /**
   * CSS 내용에서 디펜던시를 찾거나 해결한다.
   * cssContent는 style 태그의 인라인 css문자열이거나
   * css 파일의 내용일 수 있다.
   */
  private resolveDepsFromCssContents_ = (
    filePathOrElem: string | Element,
    cssContent: string,
    callback: (path: string) => string | undefined,
  ): number => {
    let newFileCnt = 0;
    const matchedResult = cssContent.match(STRING_REGEX);
    if (matchedResult) {
      matchedResult
        .filter((str) => str.match(MEDIA_FILE_QUOTED_REGEX))
        .forEach((str) => {
          const targetPath = normalizeUrlPath(str.slice(1, -1)); // remove quotes
          if (this.fileStore.has(targetPath) && !this.unresolvedDeps[targetPath]) {
            // 신규 deps 발견
            newFileCnt++;
          }
          const result = callback(targetPath);
          if (result) {
            const quoteChar = str[0]!;
            const newStr = quoteChar + result + quoteChar;
            if (typeof filePathOrElem === 'string') {
              this.updateFileContent_(filePathOrElem, (content) => content.replace(str, newStr));
            } else {
              filePathOrElem.innerHTML = filePathOrElem.innerHTML.replace(str, newStr);
            }
          }
        });
    }
    return newFileCnt;
  };

  /**
   * 스타일시트 link 태그에서 디펜던시를 찾거나 해결한다.
   */
  private resolveDepsFromStylesheetLinkTag_ = (
    doc: Document,
    callback: (path: string) => string | undefined,
  ): number => {
    let newFileCnt = 0;
    const cssLinksInHTML = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
    cssLinksInHTML.forEach((css) => {
      const href = css.getAttribute('href');
      if (href && href.match(NOT_EXTERNAL_LINK_REGEX) !== null) {
        const path = normalizeUrlPath(href);
        if (this.fileStore.has(path) && !this.unresolvedDeps[path]) {
          // 신규 deps 발견
          newFileCnt++;
        }
        const result = callback(path);
        if (result) {
          css.setAttribute('href', result);
        }
      }
    });
    return newFileCnt;
  };

  /**
   * 주어진 파일 경로의 blob url을 등록한다
   */
  private registerBlobURL_ = (filePath: string): string => {
    const file = this.fileStore.get(filePath);
    if (!file) {
      throw new Error('file does not exist:' + filePath);
    }
    const fileContent = file.content;
    const contentType = mime.getType(filePath);
    const blob = contentType
      ? new Blob([fileContent], { type: contentType })
      : new Blob([fileContent]);
    const url = URL.createObjectURL(blob);
    this.blobUrlStore.set(filePath, url);
    return url;
  };

  /**
   * 진입점이 되는 HTML 파일을 찾는다.
   * index.html이 있다면 index.html을 리턴하고
   * index.html이 없는 경우, 다른 html 파일 한개를 리턴한
   */
  static findEntryPointHtml = (filePaths: ArrayLike<string>): string | undefined => {
    let anyHtmlPath: string | undefined;

    for (let i = 0; i < filePaths.length; i++) {
      const filePath = filePaths[i]!;

      if (filePath.match(/index\.html?$/i)) {
        // index.html을 찾으면 즉시 리턴
        return filePath;
      }

      if (!anyHtmlPath) {
        if (filePath.match(/.*\.html?$/i)) {
          anyHtmlPath = filePath;
        }
      }
    }

    return anyHtmlPath;
  };
}

/**
 * 파일 경로 normalize
 * @param filePath 파일 경로
 * @returns normalized된 파일 경로
 * @example
 * normalizePath('sketch.js') => '/sketch.js'
 * normalizePath('./sketch.js') => '/sketch.js'
 * normalizePath('./test/../sketch.js') => '/sketch.js'
 */
const normalizeUrlPath = (filePath: string, origin?: string): string => {
  const url = new URL(filePath, origin || window.origin);
  return url.pathname;
};
